<template>
  <div>
    <v-card>
      <v-card-title class="d-flex align-center py-4">
        <v-btn icon @click="$emit('close')" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span>{{ $t("reservation") }} #{{ reservationId }}</span>
        <v-spacer></v-spacer>
        <v-tooltip bottom v-if="reservation && reservation.pms_id">
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              small
              v-on="on"
              :loading="reimporting"
              :disabled="reimporting"
              @click="reimportReservation"
              class="mr-1"
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("reimport") }}</span>
        </v-tooltip>
        <v-btn icon @click="$emit('close-dialog')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Loading -->
      <v-card-text v-if="loading">
        <v-progress-linear color="secondary" indeterminate></v-progress-linear>
      </v-card-text>

      <!-- Error -->
      <v-card-text v-else-if="error">
        <v-alert type="error" dense outlined>
          {{ $t("error_loading_data") }}
        </v-alert>
      </v-card-text>

      <!-- Reservation detail -->
      <v-card-text v-else-if="reservation">
        <!-- General info -->
        <div class="text-subtitle-1 font-weight-bold mb-3">
          <v-icon small class="mr-1">mdi-information-outline</v-icon>
          {{ $t("general_info") }}
        </div>
        <v-simple-table dense class="mb-4">
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="font-weight-bold" style="width: 200px;">ID</td>
                <td>{{ reservation.id }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">PMS ID</td>
                <td>{{ reservation.pms_id }}</td>
              </tr>
              <tr v-if="reservation.pms_reservation_number">
                <td class="font-weight-bold">{{ $t("reservation_number") }}</td>
                <td>{{ reservation.pms_reservation_number }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Check-in</td>
                <td>{{ reservation.start_date }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Check-out</td>
                <td>{{ reservation.end_date }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $t("creation_date") }}</td>
                <td>{{ reservation.creation_date }}</td>
              </tr>
              <tr v-if="reservation.cancellation_date">
                <td class="font-weight-bold">{{ $t("cancellation_date") }}</td>
                <td>{{ reservation.cancellation_date }}</td>
              </tr>
              <tr v-if="reservation.last_modification_date">
                <td class="font-weight-bold">{{ $t("last_modification") }}</td>
                <td>{{ reservation.last_modification_date }}</td>
              </tr>
              <tr v-if="reservation.country">
                <td class="font-weight-bold">{{ $t("country") }}</td>
                <td>
                  <span
                    v-if="reservation.country.iso"
                    class="text-uppercase mr-1"
                    >{{ reservation.country.iso }}</span
                  >
                  {{ reservation.country.name }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-divider class="my-4"></v-divider>

        <!-- Holder -->
        <div v-if="reservation.holder">
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon small class="mr-1">mdi-account</v-icon>
            {{ $t("holder") }}
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr
                  v-if="
                    reservation.holder.first_name ||
                      reservation.holder.last_name
                  "
                >
                  <td class="font-weight-bold" style="width: 200px;">
                    {{ $t("name") }}
                  </td>
                  <td>
                    {{ reservation.holder.first_name }}
                    {{ reservation.holder.last_name }}
                  </td>
                </tr>
                <tr v-if="reservation.holder.email">
                  <td class="font-weight-bold">{{ $t("email") }}</td>
                  <td>{{ reservation.holder.email }}</td>
                </tr>
                <tr v-if="reservation.holder.phone1">
                  <td class="font-weight-bold">{{ $t("phone") }}</td>
                  <td>{{ reservation.holder.phone1 }}</td>
                </tr>
                <tr v-if="reservation.holder.phone2">
                  <td class="font-weight-bold">{{ $t("phone") }} 2</td>
                  <td>{{ reservation.holder.phone2 }}</td>
                </tr>
                <tr v-if="reservation.holder.gender">
                  <td class="font-weight-bold">{{ $t("gender") }}</td>
                  <td>{{ reservation.holder.gender }}</td>
                </tr>
                <tr v-if="reservation.holder.document">
                  <td class="font-weight-bold">{{ $t("document") }}</td>
                  <td>
                    {{ reservation.holder.document }}
                    <span
                      v-if="reservation.holder.document_type"
                      class="grey--text"
                      >({{ reservation.holder.document_type }})</span
                    >
                  </td>
                </tr>
                <tr v-if="reservation.holder.country">
                  <td class="font-weight-bold">{{ $t("country") }}</td>
                  <td>
                    <span
                      v-if="reservation.holder.country.iso"
                      class="text-uppercase mr-1"
                      >{{ reservation.holder.country.iso }}</span
                    >
                    {{ reservation.holder.country.name }}
                  </td>
                </tr>
                <tr v-if="reservation.holder.city">
                  <td class="font-weight-bold">{{ $t("city") }}</td>
                  <td>{{ reservation.holder.city }}</td>
                </tr>
                <tr v-if="reservation.holder.language">
                  <td class="font-weight-bold">{{ $t("language") }}</td>
                  <td>{{ reservation.holder.language }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-divider class="my-4"></v-divider>
        </div>

        <!-- Stays -->
        <div v-if="reservation.stays && reservation.stays.length > 0">
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon small class="mr-1">mdi-bed</v-icon>
            {{ $t("stays") }} ({{ reservation.stays.length }})
          </div>

          <v-expansion-panels multiple class="mb-4">
            <v-expansion-panel
              v-for="(stay, stayIndex) in reservation.stays"
              :key="'stay-' + stayIndex"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-chip
                    v-if="stay.status"
                    x-small
                    :color="'#' + (stay.status.color || '999999')"
                    dark
                    class="mr-2"
                  >
                    {{ stay.status.name }}
                  </v-chip>
                  <span> {{ stay.start_date }} → {{ stay.end_date }} </span>
                  <span v-if="stayIntermediates(stay)" class="grey--text ml-2">
                    · {{ stayIntermediates(stay) }}
                  </span>
                  <span class="grey--text ml-2">
                    · {{ stay.nights ? stay.nights.length : 0 }}
                    {{ $t("nights") }}
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <!-- Stay general info -->
                <v-simple-table dense class="mb-3">
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold" style="width: 200px;">
                          ID
                        </td>
                        <td>{{ stay.id }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">PMS ID</td>
                        <td>{{ stay.pms_id }}</td>
                      </tr>
                      <tr v-if="stay.status">
                        <td class="font-weight-bold">{{ $t("status") }}</td>
                        <td>
                          <v-chip
                            x-small
                            :color="'#' + (stay.status.color || '999999')"
                            dark
                          >
                            {{ stay.status.name }}
                          </v-chip>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Check-in</td>
                        <td>{{ stay.start_date }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Check-out</td>
                        <td>{{ stay.end_date }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">
                          {{ $t("creation_date") }}
                        </td>
                        <td>{{ stay.creation_date }}</td>
                      </tr>
                      <tr v-if="stay.virtual !== undefined">
                        <td class="font-weight-bold">Virtual</td>
                        <td>
                          <v-icon
                            small
                            :color="stay.virtual ? 'green' : 'grey'"
                          >
                            {{ stay.virtual ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                        </td>
                      </tr>
                      <tr v-if="stay.segment">
                        <td class="font-weight-bold">{{ $t("segment") }}</td>
                        <td>{{ stay.segment.name || stay.segment }}</td>
                      </tr>
                      <tr v-if="stay.source">
                        <td class="font-weight-bold">{{ $t("source") }}</td>
                        <td>{{ stay.source.name || stay.source }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Intermediates -->
                <div
                  v-if="stay.intermediates && stay.intermediates.length > 0"
                  class="mb-3"
                >
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    {{ $t("intermediaries") }}
                  </div>
                  <v-chip
                    v-for="(inter, iIdx) in stay.intermediates"
                    :key="'inter-' + iIdx"
                    small
                    outlined
                    class="mr-1 mb-1"
                  >
                    {{ inter.name }}
                    <span v-if="inter.commission" class="ml-1 grey--text"
                      >({{ inter.commission }}%)</span
                    >
                  </v-chip>
                </div>

                <!-- Nights -->
                <div v-if="stay.nights && stay.nights.length > 0">
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    {{ $t("nights") }} ({{ stay.nights.length }})
                  </div>
                  <v-expansion-panels multiple>
                    <v-expansion-panel
                      v-for="(night, nightIndex) in stay.nights"
                      :key="'night-' + nightIndex"
                    >
                      <v-expansion-panel-header class="py-1">
                        <div class="d-flex align-center text-body-2">
                          <v-icon x-small class="mr-2"
                            >mdi-weather-night</v-icon
                          >
                          <strong class="mr-3">{{ night.date }}</strong>
                          <span v-if="nightRoom(night)" class="mr-3">
                            {{ $t("room") }}: {{ nightRoom(night) }}
                          </span>
                          <span v-if="nightRoomType(night)" class="mr-3">
                            {{ $t("type") }}: {{ nightRoomType(night) }}
                          </span>
                          <span v-if="night.board" class="mr-3">
                            {{ $t("board") }}: {{ night.board.name }}
                          </span>
                          <span class="grey--text">
                            {{ nightTotalAmount(night) }}
                          </span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-simple-table dense class="mb-2">
                          <template v-slot:default>
                            <tbody>
                              <tr>
                                <td
                                  class="font-weight-bold"
                                  style="width: 200px;"
                                >
                                  {{ $t("date") }}
                                </td>
                                <td>{{ night.date }}</td>
                              </tr>
                              <tr>
                                <td class="font-weight-bold">
                                  {{ $t("type") }}
                                </td>
                                <td>{{ night.type }}</td>
                              </tr>
                              <tr v-if="night.space && night.space.room">
                                <td class="font-weight-bold">
                                  {{ $t("room") }}
                                </td>
                                <td>
                                  {{ night.space.room.name }} ({{
                                    night.space.room.code
                                  }})
                                </td>
                              </tr>
                              <tr v-if="night.space && night.space.room_type">
                                <td class="font-weight-bold">
                                  {{ $t("room_type") }}
                                </td>
                                <td>
                                  {{ night.space.room_type.name }} ({{
                                    night.space.room_type.code
                                  }})
                                </td>
                              </tr>
                              <tr v-if="night.board">
                                <td class="font-weight-bold">
                                  {{ $t("board") }}
                                </td>
                                <td>{{ night.board.name }}</td>
                              </tr>
                              <tr v-if="night.rate_plan">
                                <td class="font-weight-bold">
                                  {{ $t("rate_plan") }}
                                </td>
                                <td>
                                  {{ night.rate_plan.name || night.rate_plan }}
                                </td>
                              </tr>
                              <tr>
                                <td class="font-weight-bold">Sleeped</td>
                                <td>
                                  <v-icon
                                    small
                                    :color="night.sleeped ? 'green' : 'grey'"
                                  >
                                    {{
                                      night.sleeped ? "mdi-check" : "mdi-close"
                                    }}
                                  </v-icon>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>

                        <!-- Persons & charges -->
                        <div
                          v-if="
                            night.night_persons &&
                              night.night_persons.length > 0
                          "
                        >
                          <div class="text-caption font-weight-bold mb-1 mt-2">
                            {{ $t("persons") }} ({{
                              night.night_persons.length
                            }})
                          </div>
                          <div
                            v-for="(np, npIdx) in night.night_persons"
                            :key="'np-' + npIdx"
                            class="mb-2"
                          >
                            <!-- Charges table -->
                            <div v-if="np.charges && np.charges.length > 0">
                              <v-simple-table dense>
                                <template v-slot:default>
                                  <thead>
                                    <tr>
                                      <th class="text-left">
                                        {{ $t("person") }} {{ npIdx + 1 }}
                                      </th>
                                      <th class="text-left">
                                        {{ $t("type") }}
                                      </th>
                                      <th class="text-right">
                                        {{ $t("net") }}
                                      </th>
                                      <th class="text-right">
                                        {{ $t("gross") }}
                                      </th>
                                      <th class="text-left">
                                        {{ $t("currency") }}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr
                                      v-for="(charge, cIdx) in np.charges"
                                      :key="'charge-' + cIdx"
                                    >
                                      <td>
                                        <v-chip
                                          x-small
                                          :color="
                                            charge.is_reservation
                                              ? 'blue'
                                              : 'orange'
                                          "
                                          dark
                                        >
                                          {{
                                            charge.is_reservation
                                              ? $t("reservation")
                                              : $t("production")
                                          }}
                                        </v-chip>
                                      </td>
                                      <td>
                                        <span v-if="charge.service">{{
                                          charge.service.name || charge.service
                                        }}</span>
                                        <span v-else class="grey--text">-</span>
                                      </td>
                                      <td
                                        v-if="
                                          charge.currency_charges &&
                                            charge.currency_charges.length > 0
                                        "
                                        class="text-right"
                                      >
                                        {{
                                          charge.currency_charges[0].net_value
                                        }}
                                      </td>
                                      <td v-else class="text-right">-</td>
                                      <td
                                        v-if="
                                          charge.currency_charges &&
                                            charge.currency_charges.length > 0
                                        "
                                        class="text-right"
                                      >
                                        {{
                                          charge.currency_charges[0].gross_value
                                        }}
                                      </td>
                                      <td v-else class="text-right">-</td>
                                      <td
                                        v-if="
                                          charge.currency_charges &&
                                            charge.currency_charges.length > 0
                                        "
                                        class="text-uppercase"
                                      >
                                        {{
                                          charge.currency_charges[0]
                                            .currency_code
                                        }}
                                      </td>
                                      <td v-else>-</td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </div>
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  props: {
    reservationId: {
      type: [Number, String],
      required: true
    }
  },
  data: () => ({
    reservation: null,
    loading: false,
    error: false,
    reimporting: false
  }),
  computed: {
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  },
  methods: {
    fetchReservation() {
      this.loading = true;
      this.error = false;
      this.$store
        .dispatch("getReservation", {
          hotel_id: this.current_hotel.id,
          reservation_id: this.reservationId
        })
        .then(response => {
          this.reservation = response;
          this.loading = false;
        })
        .catch(() => {
          this.error = true;
          this.loading = false;
        });
    },
    stayIntermediates(stay) {
      if (!stay.intermediates || stay.intermediates.length === 0) return null;
      return stay.intermediates.map(i => i.name).join(", ");
    },
    nightRoom(night) {
      if (night.space && night.space.room) {
        return night.space.room.name;
      }
      return null;
    },
    nightRoomType(night) {
      if (night.space && night.space.room_type) {
        return night.space.room_type.name;
      }
      return null;
    },
    reimportReservation() {
      const pms_id = this.reservation.pms_id;
      this.reimporting = true;
      this.$store
        .dispatch("reimportReservation", {
          hotel_id: this.current_hotel.id,
          pms_id: pms_id
        })
        .then(response => {
          this.reimporting = false;
          if (response && response.status === "ok") {
            this.$store.dispatch("setSnackbar", {
              title: pms_id,
              text: this.$t("reimport_success"),
              color: "success"
            });
            this.fetchReservation();
          } else {
            this.$store.dispatch("setSnackbar", {
              title: pms_id,
              text: response && response.message
                ? response.message
                : this.$t("reimport_error"),
              color: "error"
            });
          }
        })
        .catch(() => {
          this.reimporting = false;
          this.$store.dispatch("setSnackbar", {
            title: pms_id,
            text: this.$t("reimport_error"),
            color: "error"
          });
        });
    },
    nightTotalAmount(night) {
      if (!night.night_persons) return "";
      let total = 0;
      let currency = "";
      for (const np of night.night_persons) {
        if (!np.charges) continue;
        for (const charge of np.charges) {
          if (!charge.is_reservation || !charge.currency_charges) continue;
          for (const cc of charge.currency_charges) {
            total += parseFloat(cc.net_value) || 0;
            if (!currency) currency = cc.currency_code;
          }
        }
      }
      if (total === 0) return "";
      return total.toFixed(2) + " " + currency.toUpperCase();
    }
  },
  mounted() {
    this.fetchReservation();
  }
};
</script>
